import type { KeyboardEvent } from 'react'
import { useEffect, useId, useRef, useState } from 'react'
import { api } from '../lib/api'
import type { CompanyHit } from '../lib/types'
import { SearchIcon } from './icons'
import styles from './SearchBox.module.css'
import own from './SymbolPicker.module.css'

/** Company picker (WAI-ARIA combobox) that returns the chosen company instead of navigating. */
export function SymbolPicker({ label, onSelect, placeholder = 'Add a stock…', disabled = false }: {
  label: string; onSelect: (hit: CompanyHit) => void; placeholder?: string; disabled?: boolean
}) {
  const id = useId()
  const listId = `${id}-list`
  const [q, setQ] = useState('')
  const [hits, setHits] = useState<CompanyHit[]>([])
  const [open, setOpen] = useState(false)
  const [active, setActive] = useState(-1)
  const [state, setState] = useState<'idle' | 'loading' | 'done' | 'error'>('idle')
  const seq = useRef(0)

  useEffect(() => {
    const term = q.trim()
    const mine = ++seq.current
    if (term.length < 1) { setHits([]); setState('idle'); return }
    const t = setTimeout(() => {
      setState('loading'); setOpen(true)
      api<{ data: CompanyHit[] }>(`/companies/search?q=${encodeURIComponent(term)}&limit=8`)
        .then(r => { if (mine === seq.current) { setHits(r.data); setState('done'); setActive(r.data.length ? 0 : -1) } })
        .catch(() => { if (mine === seq.current) { setHits([]); setState('error') } })
    }, 150)
    return () => clearTimeout(t)
  }, [q])

  const pick = (h?: CompanyHit) => { if (!h) return; setOpen(false); setQ(''); setHits([]); setState('idle'); onSelect(h) }
  const onKey = (e: KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'ArrowDown') { e.preventDefault(); setOpen(true); setActive(a => Math.min(hits.length - 1, a + 1)) }
    else if (e.key === 'ArrowUp') { e.preventDefault(); setActive(a => Math.max(0, a - 1)) }
    else if (e.key === 'Enter') { e.preventDefault(); pick(hits[active]) }
    else if (e.key === 'Escape') { setOpen(false) }
  }
  const expanded = open && hits.length > 0
  // Status line under the box while typing: searching, no match or a failed request (a silent box looks broken).
  const status = !open || !q.trim() ? null : state === 'loading' && !hits.length ? 'Searching…'
    : state === 'done' && !hits.length ? `No NSE/BSE company matches “${q.trim()}”` : state === 'error' ? 'Search failed — try again' : null
  return (
    <div className={own.wrap}>
      <div className={styles.box}>
        <SearchIcon size={16} />
        <label htmlFor={id} className="sr-only">{label}</label>
        <input id={id} type="text" role="combobox" aria-autocomplete="list" aria-expanded={expanded} aria-controls={listId}
          aria-activedescendant={expanded && active >= 0 ? `${id}-opt-${active}` : undefined} autoComplete="off"
          placeholder={placeholder} value={q} disabled={disabled}
          onChange={e => setQ(e.target.value)} onKeyDown={onKey}
          onFocus={() => hits.length && setOpen(true)} onBlur={() => setTimeout(() => setOpen(false), 150)} />
      </div>
      <ul id={listId} role="listbox" aria-label={`${label}: matches`} className={styles.list} hidden={!expanded}>
        {hits.map((h, i) => (
          <li key={h.symbol} id={`${id}-opt-${i}`} role="option" aria-selected={i === active}
            className={`${styles.opt} ${i === active ? styles.active : ''}`}
            onMouseDown={e => { e.preventDefault(); pick(h) }} onMouseEnter={() => setActive(i)}>
            <span className={styles.sym}>{h.symbol}</span><span className={styles.name}>{h.name}</span>
          </li>
        ))}
      </ul>
      {status && <div className={styles.list} role="status" style={{ padding: '10px 12px', color: 'var(--muted)', fontSize: 'var(--fs-table)' }}>{status}</div>}
    </div>
  )
}
